import { 
  Box, 
  Divider, 
  useColorModeValue, 
  SimpleGrid,
  Container,
  VStack,
  Icon,
  Text,
  Flex
} from '@chakra-ui/react';
import { Scroll, MapPin, Compass } from '@phosphor-icons/react';

// Import section components using the index file
import {
  AboutHeroSection,
  MissionSection,
  ProblemsSolvedSection,
  FeaturesSection,
  TechnologySection,
  AboutCTASection
} from '../components/about'; // Updated import path

// Simple Icon Divider Component
const ThemedDivider = ({ showIcons = true }) => {
  const iconColor = useColorModeValue('interactive.primary', 'interactive.primary');
  const dividerColor = useColorModeValue('border.default', 'border.default');
  
  return (
    <Box position="relative" my={{ base: 16, md: 20 }}>
      {showIcons && (
        <Flex align="center" justify="center">
          <Icon as={MapPin} boxSize={5} color={iconColor} opacity={0.7} />
          <Box w="60px" h="1px" bg={dividerColor} mx={3} opacity={0.5} />
          <Icon as={Compass} boxSize={6} color={iconColor} opacity={0.8} />
          <Box w="60px" h="1px" bg={dividerColor} mx={3} opacity={0.5} />
          <Icon as={Scroll} boxSize={5} color={iconColor} opacity={0.7} />
        </Flex>
      )}
    </Box>
  );
};


export default function About() {
  const pageNumberBg = useColorModeValue('rgba(240, 230, 210, 0.8)', 'rgba(44, 35, 27, 0.7)');
  const pageNumberBorder = useColorModeValue('border.light', 'border.light');
  const pageNumberColor = useColorModeValue('text.muted', 'text.muted');
  const spineGradient = useColorModeValue(
    'linear(to-r, colors.border.heavy, colors.border.default, colors.border.light)',
    'linear(to-r, colors.border.heavy, colors.border.default, colors.border.light)'
  );

  return (
    <Box 
      position="relative" 
      bg={useColorModeValue('bg.default', 'bg.default')}
      minH="100vh"
      _before={{
        content: '""',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.03, 0.05),
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '600px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
    >
      {/* Enhanced Journal Binding / Spine */}
      <Box
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        width={{ base: "6px", md: "18px" }}
        bgGradient={spineGradient}
        boxShadow="3px 0 8px rgba(0,0,0,0.2)"
        zIndex={15}
        display={{ base: 'none', md: 'block' }}
        _before={{
          content: '""',
          position: 'absolute',
          left: '5px',
          top: '5%',
          bottom: '5%',
          width: '4px',
          bgColor: 'rgba(255,255,255,0.15)',
          borderRadius: 'full',
          boxShadow: 'inset 1px 0 2px rgba(0,0,0,0.2)'
        }}
        _after={{
          content: '""',
          position: 'absolute',
          left: '12px',
          top: '8%',
          bottom: '8%',
          width: '2px',
          bgColor: 'rgba(255,255,255,0.2)',
          opacity: 0.8,
          borderRadius: 'full'
        }}
      />

      {/* Enhanced Page Marker */}
      <Box
        position="fixed"
        bottom="30px"
        left={{ base: "15px", md: "35px" }}
        fontFamily="handwritten"
        fontSize={{ base: "sm", md: "md" }}
        color={pageNumberColor}
        fontWeight="medium"
        zIndex={10}
        display={{ base: 'block', md: 'block' }}
        p={{ base: "4px 10px", md: "6px 12px" }}
        bg={pageNumberBg}
        borderRadius="full"
        boxShadow="md"
        border="2px solid"
        borderColor={pageNumberBorder}
        transform="rotate(-2deg)"
        _hover={{ transform: "rotate(0deg)", transition: "all 0.3s ease" }}
      >
        <Flex align="center" gap={2}>
          <Icon as={Scroll} boxSize={4} />
          <Text>About</Text>
        </Flex>
      </Box>

      {/* Main Content Area with Enhanced Layout */}
      <Box pl={{ base: 0, md: "20px" }} position="relative" zIndex={1}>
        <AboutHeroSection />

        <ThemedDivider />

        {/* Enhanced Mission and Technology Layout */}
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <SimpleGrid 
            columns={{ base: 1, lg: 2 }} 
            spacing={{ base: 12, lg: 20 }} 
            alignItems="start"
          >
            <Box>
              <MissionSection />
            </Box>
            <Box>
              <TechnologySection />
            </Box>
          </SimpleGrid>
        </Container>

        <ThemedDivider />

        <ProblemsSolvedSection />

        <ThemedDivider />

        <FeaturesSection />

        <ThemedDivider />

        <AboutCTASection />

        {/* Bottom Decorative Element */}
        <Box
          py={8}
          textAlign="center"
          position="relative"
        >
          <Flex align="center" justify="center" opacity={0.5}>
            <Icon as={MapPin} boxSize={4} color={useColorModeValue('interactive.primary', 'interactive.primary')} />
            <Box w="100px" h="1px" bg={useColorModeValue('border.default', 'border.default')} mx={4} />
            <Text fontFamily="body" fontSize="sm" color={useColorModeValue('text.muted', 'text.muted')}>
              End of Page
            </Text>
            <Box w="100px" h="1px" bg={useColorModeValue('border.default', 'border.default')} mx={4} />
            <Icon as={Compass} boxSize={4} color={useColorModeValue('interactive.primary', 'interactive.primary')} />
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
